import { useLayoutEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Login from "./pages/login";
import DeviceSettings from "./pages/admin/deviceSettings";
import FullScreen from "./pages/admin/fullScreen";
import ManufacturingStart from "./pages/admin/manufacturingStart";
import CycleTimeSettings from "./pages/admin/cycletimeSettings";
import SkillCheck from "./pages/admin/skillCheck";
import SkillMap from "pages/admin/skillMap";
import Work from "./pages/admin/working"
import Reset from "./pages/admin/reset"
import { useSelector } from "react-redux";
import { RootState } from "store";

const queryClient = new QueryClient();

const BASE_W = 1280;
const BASE_H = 800;

interface PrivateRouteProps {
  isLogined: boolean;
  children: JSX.Element;
}

function App() {
  const rootRef = useRef<HTMLDivElement>(null);
  const { isLoading } = useSelector((state: RootState) => state.common);
  const { isAdminLoggedIn } = useSelector((state: RootState) => state.adminAuth);

  const PrivateRoute = ({ isLogined, children }: PrivateRouteProps) => {
    if (!isLogined) {
      window.location.href = "/admin/login";
    }

    return children;
  };

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const applyScale = () => {
      const s = Math.min(window.innerWidth / BASE_W, window.innerHeight / BASE_H);
      const offsetX = (window.innerWidth - BASE_W * s) / 2;
      const offsetY = (window.innerHeight - BASE_H * s) / 2;
      el.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${s})`;
    };
    applyScale();
    window.addEventListener("resize", applyScale);
    return () => window.removeEventListener("resize", applyScale);
  }, []);

  return (
    <div
      id="canvas-root"
      ref={rootRef}
      style={{
        position: "fixed",
        top: 0, left: 0,
        width: BASE_W, height: BASE_H,
        transformOrigin: "top left",
      }}
    >
      <QueryClientProvider client={queryClient}>
        <Router>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
            // onClick={handleClose}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <div>
            <Routes>
              <Route path="/admin/login" element={<Login />} />
              <Route path="/" element={<Login />} />
              <Route path="/admin/device-settings" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <DeviceSettings />
                </PrivateRoute>
              } />
              <Route path="/admin/full-screen" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <FullScreen />
                </PrivateRoute>
              } />
              <Route path="/admin/manufacturing-start" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <ManufacturingStart />
                </PrivateRoute>
              } />
              <Route path="/admin/cycle-time-settings" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <CycleTimeSettings />
                </PrivateRoute>
              } />
              <Route path="/admin/skill-check" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <SkillCheck />
                </PrivateRoute>
              } />
              <Route path="/admin/skill-map" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <SkillMap />
                </PrivateRoute>
              } />
              <Route path="/admin/work" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <Work />
                </PrivateRoute>
              } />
              <Route path="/admin/reset" element={
                <PrivateRoute isLogined={isAdminLoggedIn}>
                  <Reset />
                </PrivateRoute>
              } />

            </Routes>
          </div>
        </Router>
      </QueryClientProvider>
    </div>
  );
}

export default App;
